import { ButtonDnD } from '@/components/ButtonDnD'
import { ButtonInCallOptions } from '@/components/ButtonInCallOptions'
import { ButtonMicrophone } from '@/components/ButtonMicrophone'
import { ButtonRaiseHand } from '@/components/ButtonRaiseHand'
import { ButtonShareScreen } from '@/components/ButtonShareScreen'
import { ButtonVideo } from '@/components/ButtonVideo'
import { DialogInCallOptions } from '@/components/DialogInCallOptions'
import { LinkLeave } from '@/components/LinkLeave'
import type { RedirectPath } from '@/helpers/types'
import { useSelfDNDButtonState } from '@/hooks/useSelfDNDButtonState'
import { useSelfMicrophoneButtonState } from '@/hooks/useSelfMicrophoneButtonState'
import { useSelfHandRaiseButtonState } from '@/hooks/useSelfRaiseHandButtonState'
import { useSelfScreenShareButtonState } from '@/hooks/useSelfScreenShareButtonState'
import { useSelfVideoButtonState } from '@/hooks/useSelfVideoButtonState'
import { useRoomStore } from '@/stores/room'

interface RoomFooterInlineProps {
  redirect: RedirectPath | undefined
  totalFooterButtonCount: number
}

export const RoomFooterInline = ({
  redirect,
  totalFooterButtonCount,
}: RoomFooterInlineProps) => {
  const callState = useRoomStore(state => state.callState)

  const { isDNDButtonEnabled, isDNDButtonVisible } = useSelfDNDButtonState()

  const { isRaiseHandButtonEnabled, isRaiseHandButtonVisible } =
    useSelfHandRaiseButtonState()

  const { isMicrophoneButtonVisible, isMicrophoneEnabled } =
    useSelfMicrophoneButtonState()

  const { isScreenShareButtonEnabled, isScreenShareButtonVisible } =
    useSelfScreenShareButtonState()

  const { isVideoButtonEnabled, isVideoButtonVisible } =
    useSelfVideoButtonState()

  const callJoined = callState === 'joined'

  /* eslint-disable sort-keys */
  const showButtonsByPriorityMap = {
    shareScreen: isScreenShareButtonVisible && totalFooterButtonCount >= 5,
    raiseHand: isRaiseHandButtonVisible && totalFooterButtonCount >= 4,
    dnd: isDNDButtonVisible && totalFooterButtonCount >= 3,
    video: isVideoButtonVisible && totalFooterButtonCount >= 2,
    microphone: isMicrophoneButtonVisible && totalFooterButtonCount >= 1,
  } as const
  /* eslint-enable sort-keys */

  // TODO: use css container query to show options button
  return (
    <div
      aria-label="In call controls"
      className="flex items-center justify-end gap-x-2"
      role="group"
    >
      <DialogInCallOptions
        // NOTE: show a button in dialog if it is not visible in the footer
        showDND={!showButtonsByPriorityMap.dnd}
        showMicrophone={!showButtonsByPriorityMap.microphone}
        showRaiseHand={!showButtonsByPriorityMap.raiseHand}
        showScreenShare={!showButtonsByPriorityMap.shareScreen}
        showVideo={!showButtonsByPriorityMap.video}
      >
        <ButtonInCallOptions disabled={!callJoined} />
      </DialogInCallOptions>

      <ButtonShareScreen
        disabled={!callJoined || !isScreenShareButtonEnabled}
        isVisible={showButtonsByPriorityMap.shareScreen}
      />
      <ButtonRaiseHand
        disabled={!callJoined || !isRaiseHandButtonEnabled}
        isVisible={showButtonsByPriorityMap.raiseHand}
      />
      <ButtonDnD
        disabled={true || !isDNDButtonEnabled}
        isVisible={showButtonsByPriorityMap.dnd}
      />
      <ButtonVideo
        disabled={!callJoined || !isVideoButtonEnabled}
        isVisible={showButtonsByPriorityMap.video}
      />
      <ButtonMicrophone
        disabled={!callJoined || !isMicrophoneEnabled}
        isVisible={showButtonsByPriorityMap.microphone}
      />
      <LinkLeave to={redirect}>Leave</LinkLeave>
    </div>
  )
}
